import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/context/auth-context'
import { useToast } from '@/hooks/use-toast'
import { Building2, User, Shield, Bell, Key, Loader2, Copy, Check, Hash } from 'lucide-react'

export default function ProviderSettingsPage() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [copiedId, setCopiedId] = useState(false)

  const profile = user?.profile as any
  
  // Provider-specific data comes from type_specific_data spread into profile
  const institutionName = profile?.institution_name || profile?.institutionName || ''
  const institutionType = profile?.institution_type || profile?.institutionType || ''
  const providerId = profile?.providerId || ''
  const contactFirstName = profile?.contact_first_name || profile?.contactFirstName || profile?.firstName || ''
  const contactLastName = profile?.contact_last_name || profile?.contactLastName || profile?.lastName || ''
  const contactTitle = profile?.contact_title || profile?.contactTitle || ''
  const institutionAddress = profile?.institution_address || profile?.institutionAddress || {}
  const phone = profile?.phone || ''
  const website = profile?.website || ''
  const specialties = profile?.specialties || []
  const isInstitutionVerified = profile?.is_institution_verified || profile?.isInstitutionVerified || false

  const handleCopyProviderId = async () => {
    if (providerId) {
      try {
        await navigator.clipboard.writeText(providerId)
        setCopiedId(true)
        toast({
          title: "Provider ID Copied",
          description: "Your Provider ID has been copied to clipboard.",
        })
        setTimeout(() => setCopiedId(false), 2000)
      } catch (error) {
        toast({
          title: "Copy Failed",
          description: "Could not copy Provider ID. Please try again.",
          variant: "destructive",
        })
      }
    }
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
      // TODO: Implement settings update API
      await new Promise(resolve => setTimeout(resolve, 1000))
      toast({
        title: "Settings saved",
        description: "Your settings have been updated successfully.",
      })
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Could not save settings. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your provider account settings</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[#D64463]" />
            Institution Information
          </CardTitle>
          <CardDescription>Your healthcare institution details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Institution Name</label>
              <Input value={institutionName} disabled className="mt-1 bg-gray-50" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Institution Type</label>
              <Input value={institutionType} disabled className="mt-1 bg-gray-50 capitalize" />
            </div>
          </div>
          
          {/* Address */}
          {institutionAddress && (institutionAddress.street || institutionAddress.city) && (
            <div>
              <label className="text-sm font-medium text-gray-700">Address</label>
              <Input 
                value={[
                  institutionAddress.street,
                  institutionAddress.city,
                  institutionAddress.state,
                  institutionAddress.zipCode,
                  institutionAddress.country
                ].filter(Boolean).join(', ')} 
                disabled 
                className="mt-1 bg-gray-50" 
              />
            </div>
          )}
          
          {/* Phone & Website */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {phone && (
              <div>
                <label className="text-sm font-medium text-gray-700">Phone</label>
                <Input value={phone} disabled className="mt-1 bg-gray-50" />
              </div>
            )}
            {website && (
              <div>
                <label className="text-sm font-medium text-gray-700">Website</label>
                <Input value={website} disabled className="mt-1 bg-gray-50" />
              </div>
            )}
          </div>
          
          {/* Specialties */}
          {specialties && specialties.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700">Specialties</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {specialties.map((specialty: string, idx: number) => (
                  <Badge key={idx} variant="secondary" className="bg-gray-100">
                    {specialty}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2 pt-2">
            <Shield className={`h-4 w-4 ${isInstitutionVerified ? 'text-emerald-600' : 'text-amber-600'}`} />
            <span className="text-sm text-gray-600">Verification Status:</span>
            <Badge className={isInstitutionVerified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {isInstitutionVerified ? 'Verified' : 'Pending Verification'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Provider ID Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Hash className="h-5 w-5 text-[#D64463]" />
            Provider ID
          </CardTitle>
          <CardDescription>Your unique provider identifier for patient connections</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Your Provider ID</p>
            <div className="flex items-center justify-between">
              <code className="text-xl font-mono font-bold text-[#0F172A]">
                {providerId || 'Not assigned'}
              </code>
              {providerId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyProviderId}
                  className="flex items-center gap-2 hover:bg-[#0F172A] hover:text-white transition-colors"
                >
                  {copiedId ? (
                    <>
                      <Check className="h-4 w-4 text-emerald-500" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy ID
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Share this ID with patients so they can connect their CareVault app to your organization. 
            Patients will use this ID when granting you access to their health records.
          </p>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5 text-[#D64463]" />
            Contact Information
          </CardTitle>
          <CardDescription>Primary contact for this provider account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">First Name</label>
              <Input value={contactFirstName} disabled className="mt-1 bg-gray-50" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Last Name</label>
              <Input value={contactLastName} disabled className="mt-1 bg-gray-50" />
            </div>
          </div>
          {contactTitle && (
            <div>
              <label className="text-sm font-medium text-gray-700">Title</label>
              <Input value={contactTitle} disabled className="mt-1 bg-gray-50" />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700">Email Address</label>
            <Input value={user?.email || ''} disabled className="mt-1 bg-gray-50" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#D64463]" />
            Notifications
          </CardTitle>
          <CardDescription>Configure how you receive notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">New Patient Shares</p>
              <p className="text-sm text-gray-500">Get notified when a patient shares records</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">Enabled</Badge>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
            <div>
              <p className="font-medium text-gray-900">Share Expirations</p>
              <p className="text-sm text-gray-500">Alerts when shared access is about to expire</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">Enabled</Badge>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="h-5 w-5 text-[#D64463]" />
            Security
          </CardTitle>
          <CardDescription>Manage your account security</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button variant="outline" className="w-full justify-start">
            Change Password
          </Button>
          <Button variant="outline" className="w-full justify-start">
            Enable Two-Factor Authentication
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={isLoading}
          className="bg-gradient-to-r from-[#D64463] to-[#9C27B0] hover:opacity-90"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  )
}
